import { cn } from '@mdxui/primitives/lib/utils'
import { useState, type ComponentType, type ReactNode } from 'react'
import { BlogCard, type BlogPost } from '../components/blog-card'
import { BlogGrid } from '../components/blog-grid'
import { BlogHeader } from '../components/blog-header'
import { TagFilter } from '../components/tag-filter'
import { ViewModeToggle, type ViewMode } from '../components/view-mode-toggle'

export interface BlogPageLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface BlogPageProps {
  /** Blog title */
  title: string
  /** Blog description */
  description?: string
  /** Array of blog posts */
  posts: BlogPost[]
  /** Currently selected tag (for filtering) */
  selectedTag?: string
  /** Callback when tag is selected (callback mode) */
  onTagSelect?: (tag: string) => void
  /** Show tag filter */
  showTagFilter?: boolean
  /** External view mode — when provided, skips internal useState (server component friendly) */
  viewMode?: ViewMode
  /** Callback when view mode changes (callback mode) */
  onViewModeChange?: (mode: ViewMode) => void
  /** Link mode — returns href for each tag (used with LinkComponent) */
  tagHref?: (tag: string) => string
  /** Link mode — returns href for each view mode (used with LinkComponent) */
  viewModeHref?: (mode: ViewMode) => string
  /** Custom link component for framework routing */
  LinkComponent?: ComponentType<BlogPageLinkProps>
  /** Base path for post links (default: '') — e.g. '/blog' produces '/blog/{slug}' */
  basePath?: string
  /** Custom date formatter */
  formatDate?: (date: string) => string
  /** Additional className */
  className?: string
}

/**
 * Extract unique tags from posts (max 6 including "All")
 */
function extractTags(posts: BlogPost[]): string[] {
  const tagCounts = new Map<string, number>()
  for (const post of posts) {
    if (post.tags) {
      for (const tag of post.tags) {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1)
      }
    }
  }
  // Sort by frequency (desc), then alphabetically for ties. Limit to 5 + "All" = 6 total
  const sortedTags = Array.from(tagCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 5)
    .map(([tag]) => tag)
  return ['All', ...sortedTags]
}

/**
 * Calculate tag counts
 */
function getTagCounts(posts: BlogPost[], tags: string[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const tag of tags) {
    if (tag === 'All') {
      counts[tag] = posts.length
    } else {
      counts[tag] = posts.filter((post) => post.tags?.includes(tag)).length
    }
  }
  return counts
}

/**
 * Filter posts by tag
 */
function filterPosts(posts: BlogPost[], tag: string): BlogPost[] {
  if (tag === 'All') return posts
  return posts.filter((post) => post.tags?.includes(tag))
}

/**
 * Sort posts by date (newest first)
 */
function sortPosts(posts: BlogPost[]): BlogPost[] {
  return [...posts].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0
    const dateB = b.date ? new Date(b.date).getTime() : 0
    return dateB - dateA
  })
}

/**
 * Blog page component
 *
 * Supports two modes:
 * 1. **Callback mode** (client): `onTagSelect` + `onViewModeChange` — uses internal useState for view mode
 * 2. **Link mode** (server): `tagHref` + `viewModeHref` + `LinkComponent` + `viewMode` prop — pure server component, URL-driven
 *
 * @example
 * ```tsx
 * // Server component (link mode)
 * <BlogPage
 *   title="Blog"
 *   posts={posts}
 *   showTagFilter
 *   selectedTag={selectedTag}
 *   viewMode={viewMode}
 *   tagHref={(tag) => `/blog?tag=${tag}`}
 *   viewModeHref={(mode) => `/blog?view=${mode}`}
 *   LinkComponent={Link}
 * />
 *
 * // Client component (callback mode)
 * <BlogPage
 *   title="Blog"
 *   posts={posts}
 *   showTagFilter
 *   selectedTag="All"
 *   onTagSelect={(tag) => console.log('Selected:', tag)}
 * />
 * ```
 */
export function BlogPage({
  title,
  description,
  posts,
  selectedTag = 'All',
  onTagSelect,
  showTagFilter = false,
  viewMode: viewModeProp,
  onViewModeChange,
  tagHref,
  viewModeHref,
  LinkComponent,
  basePath = '',
  formatDate,
  className,
}: BlogPageProps) {
  // Use external viewMode if provided (server component), otherwise internal state (client component)
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('card')
  const viewMode = viewModeProp ?? internalViewMode
  const handleViewModeChange = onViewModeChange ?? setInternalViewMode

  const sortedPosts = sortPosts(posts)
  const tags = extractTags(sortedPosts)
  const tagCounts = getTagCounts(sortedPosts, tags)
  const filteredPosts = filterPosts(sortedPosts, selectedTag)

  const hasTagInteraction = onTagSelect || tagHref

  return (
    <main className={cn('min-h-screen bg-background relative max-w-7xl mx-auto', className)}>
      <BlogHeader title={title} description={description}>
        {showTagFilter && tags.length > 1 && hasTagInteraction && (
          <div className='max-w-7xl mx-auto w-full flex items-center justify-between gap-4'>
            <TagFilter
              tags={tags}
              selectedTag={selectedTag}
              onTagSelect={onTagSelect}
              tagHref={tagHref}
              LinkComponent={LinkComponent}
              tagCounts={tagCounts}
            />
            <ViewModeToggle
              viewMode={viewMode}
              onViewModeChange={!viewModeHref ? handleViewModeChange : undefined}
              viewModeHref={viewModeHref}
              LinkComponent={LinkComponent}
            />
          </div>
        )}
      </BlogHeader>

      {filteredPosts.length > 0 ?
        <BlogGrid
          items={filteredPosts}
          viewMode={viewMode}
          renderItem={(post) => <BlogCard key={post.slug} post={post} LinkComponent={LinkComponent} basePath={basePath} formatDate={formatDate} />}
          formatDate={formatDate}
          LinkComponent={LinkComponent}
          basePath={basePath}
        />
      : <div className='max-w-7xl mx-auto px-6 py-16 text-center'>
          <p className='text-muted-foreground text-lg'>
            {selectedTag !== 'All' ? `No posts found with tag "${selectedTag}"` : 'No blog posts available'}
          </p>
        </div>
      }
    </main>
  )
}

// Re-export BlogPost type for convenience (BlogPostAuthor available via components)
export type { BlogPost } from '../components/blog-card'
